/**
 * @fileoverview One curriculum item of the teacher assessment, read-only, in the My tasks look
 * (AssessmentScoreItem): color-coded card, the "hands" picker frozen on the teacher's score, the rubric of
 * that score, the teacher's comment and the student's own score for comparison.
 */

import { Badge, Box, Paper, Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { ScorePicker } from 'pages/StudentTasks/components/ScorePicker'
import type { TeacherScoreItem } from './types'

const ACCENT_BORDER_WIDTH = 4
const NOOP = () => {}

type Accent = 'blue' | 'green'

function rubricFor(item: TeacherScoreItem, score: number, fallback: (score: number) => string): string {
  const fromRubrics = item.rubrics?.[score]
  if (fromRubrics) return fromRubrics
  const key = `score${score}Rubric` as keyof NonNullable<TeacherScoreItem['lv4Item']>
  return item.lv4Item?.[key] || fallback(score)
}

/** Rubrics keyed "1".."5" for the picker tooltips (item-specific first, generic translation after). */
function rubricMap(item: TeacherScoreItem, fallback: (score: number) => string): Record<string, string> {
  return Object.fromEntries([1, 2, 3, 4, 5].map(score => [String(score), rubricFor(item, score, fallback)]))
}

function breadcrumbOf(item: TeacherScoreItem): string {
  const parts = typeof item.breadcrumb === 'string' ? item.breadcrumb.split('>').map(s => s.trim()) : []
  const lv2 = item.lv2Name || parts[1]
  const lv3 = item.lv3Name || parts[2]
  return [item.lv1Name, lv2, lv3].filter(Boolean).join(' › ')
}

export function TeacherScoreItemCard({ item, color }: { item: TeacherScoreItem; color: Accent }) {
  const { t } = useTranslation()
  const tr = (key: string, options?: object) => t(`student_task_tracker.teacher_drawer.${key}`, options)
  const fallbackRubric = (score: number) => t(`rubric_${score}`)
  const teacherScore = item.masterScore ?? item.score ?? 0
  const studentScore = item.studentScore ?? 0
  const name = item.name ?? item.lv4Item?.name ?? ''
  const justification = item.justification?.trim() ?? ''
  const breadcrumb = breadcrumbOf(item)

  return (
    <Paper
      withBorder
      radius='md'
      p='md'
      style={item.color ? { borderLeft: `${ACCENT_BORDER_WIDTH}px solid ${item.color}` } : undefined}
    >
      <Stack spacing='sm'>
        <Stack spacing={2}>
          {breadcrumb && (
            <Text size='xs' color='dimmed'>
              {breadcrumb}
            </Text>
          )}
          <Text size='sm' weight={500}>
            {item.originalCodes && (
              <Text span weight={700} mr={6}>
                {item.originalCodes}
              </Text>
            )}
            {name}
          </Text>
        </Stack>

        {item.doNotAssess ? (
          <Badge color='gray' variant='light' sx={{ alignSelf: 'flex-start' }}>
            {tr('not_assessed')}
          </Badge>
        ) : teacherScore > 0 ? (
          <Stack spacing='xs'>
            <Text size='xs' color='dimmed' weight={600}>
              {tr('teacher_score')}
            </Text>
            {/* Read-only: the picker is the same one the student uses, frozen on the teacher's score. */}
            <Box sx={{ pointerEvents: 'none' }} aria-label={tr('teacher_score')}>
              <ScorePicker
                value={teacherScore}
                onChange={NOOP}
                color={color}
                rubrics={rubricMap(item, fallbackRubric)}
              />
            </Box>
            <Text size='sm' color='dimmed'>
              {rubricFor(item, teacherScore, fallbackRubric)}
            </Text>
            {justification && (
              <Text size='sm' sx={{ whiteSpace: 'pre-line' }}>
                <Text span weight={600}>
                  {tr('justification')}:{' '}
                </Text>
                {justification}
              </Text>
            )}
          </Stack>
        ) : (
          <Text size='sm' color='dimmed' italic>
            {tr('pending')}
          </Text>
        )}

        {studentScore > 0 && (
          <Text size='xs' color='dimmed'>
            {tr('your_score', { score: studentScore })}
          </Text>
        )}
      </Stack>
    </Paper>
  )
}
